<template>
  <div :class="['footer-container', { collapsed: collapsed} ]" :style="footerStyle">
    <div class="input-container"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      :class="{ 'drag-over': isDragOver }">

      <!-- File preview bar -->
      <div v-if="selectedFile" class="file-preview-bar">
        <div class="file-preview-content">
          <img v-if="filePreview" :src="filePreview" class="file-preview-thumb" alt="Preview" />
          <span v-else class="file-preview-icon">&#128196;</span>
          <span class="file-preview-name">{{ selectedFile.name }}</span>
        </div>
        <button class="file-preview-remove" @click="removeFile">&times;</button>
      </div>

      <div class="input-content">
        <!-- Upload icon (only for assistants that support file upload) -->
        <div v-if="supportsUpload" class="icon-container">
          <div class="icon upload-icon" @click="triggerFileInput">
            <img
              :src="require('@/assets/icons/plus-icon.svg')"
              alt="Upload file" />
          </div>
        </div>

        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          :accept="acceptedFileTypes"
          style="display: none"
          @change="onFileSelected"
        />

        <!-- Thinking dots while waiting for API -->
        <div v-if="isThinking" class="thinking-indicator">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        </div>

        <!-- Multiline textbox -->
        <div
          ref="editor"
          :contenteditable="!isDisabled"
          class="input-text"
          @input="onInput"
          @keydown.enter.exact.prevent="sendMessage"
          @paste="onPaste"
          :data-placeholder="isDisabled ? '' : 'Ask ' + companyInformation.product_name"
        ></div>

        <!-- Voice controls group (desktop only) -->
        <div v-if="showVoiceControls && (speechSupported || ttsSupported)" class="voice-controls">
          <!-- Microphone button (primary action) -->
          <div
            v-if="speechSupported"
            class="voice-control"
            @click="toggleRecording"
            :title="isRecording ? 'Stop recording' : (autoVoiceInput ? 'Start continuous listening' : 'Start voice input')"
          >
            <div class="voice-control-icon mic-icon" :class="{ 'mic-active': isRecording, 'mic-icon-hover': !isRecording }">
              <svg v-if="!isRecording" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 13.75C10.9942 13.749 11.9475 13.3535 12.6505 12.6505C13.3535 11.9475 13.749 10.9942 13.75 10V5C13.75 4.00544 13.3549 3.05161 12.6517 2.34835C11.9484 1.64509 10.9946 1.25 10 1.25C9.00544 1.25 8.05161 1.64509 7.34835 2.34835C6.64509 3.05161 6.25 4.00544 6.25 5V10C6.25103 10.9942 6.64645 11.9475 7.34949 12.6505C8.05253 13.3535 9.00576 13.749 10 13.75ZM7.5 5C7.5 4.33696 7.76339 3.70107 8.23223 3.23223C8.70107 2.76339 9.33696 2.5 10 2.5C10.663 2.5 11.2989 2.76339 11.7678 3.23223C12.2366 3.70107 12.5 4.33696 12.5 5V10C12.5 10.663 12.2366 11.2989 11.7678 11.7678C11.2989 12.2366 10.663 12.5 10 12.5C9.33696 12.5 8.70107 12.2366 8.23223 11.7678C7.76339 11.2989 7.5 10.663 7.5 10V5ZM10.625 16.2188V18.125C10.625 18.2908 10.5592 18.4497 10.4419 18.5669C10.3247 18.6842 10.1658 18.75 10 18.75C9.83424 18.75 9.67527 18.6842 9.55806 18.5669C9.44085 18.4497 9.375 18.2908 9.375 18.125V16.2188C7.83409 16.062 6.40607 15.3393 5.36707 14.1907C4.32806 13.042 3.7519 11.5489 3.75 10C3.75 9.83424 3.81585 9.67527 3.93306 9.55806C4.05027 9.44085 4.20924 9.375 4.375 9.375C4.54076 9.375 4.69973 9.44085 4.81694 9.55806C4.93415 9.67527 5 9.83424 5 10C5 11.3261 5.52678 12.5979 6.46447 13.5355C7.40215 14.4732 8.67392 15 10 15C11.3261 15 12.5979 14.4732 13.5355 13.5355C14.4732 12.5979 15 11.3261 15 10C15 9.83424 15.0658 9.67527 15.1831 9.55806C15.3003 9.44085 15.4592 9.375 15.625 9.375C15.7908 9.375 15.9497 9.44085 16.0669 9.55806C16.1842 9.67527 16.25 9.83424 16.25 10C16.2481 11.5489 15.6719 13.042 14.6329 14.1907C13.5939 15.3393 12.1659 16.062 10.625 16.2188Z" fill="currentColor"/>
              </svg>
              <!-- Stop icon when recording -->
              <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4" width="12" height="12" rx="2" fill="currentColor"/>
              </svg>
            </div>
            <span class="voice-control-label">{{ isRecording ? 'Stop' : 'Dictate' }}</span>
          </div>

          <!-- Auto voice input toggle -->
          <div
            v-if="speechSupported"
            class="voice-control"
            @click="toggleAutoVoice"
            :title="autoVoiceInput ? 'Disable continuous listening' : 'Enable continuous listening'"
          >
            <div class="voice-control-icon auto-voice-toggle" :class="{ 'auto-voice-active': autoVoiceInput }">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 11.5C10.663 11.5 11.2989 11.2366 11.7678 10.7678C12.2366 10.2989 12.5 9.663 12.5 9V5C12.5 4.337 12.2366 3.70107 11.7678 3.23223C11.2989 2.76339 10.663 2.5 10 2.5C9.337 2.5 8.70107 2.76339 8.23223 3.23223C7.76339 3.70107 7.5 4.337 7.5 5V9C7.5 9.663 7.76339 10.2989 8.23223 10.7678C8.70107 11.2366 9.337 11.5 10 11.5Z" fill="currentColor"/>
                <path d="M14 9C14 10.0609 13.5786 11.0783 12.8284 11.8284C12.0783 12.5786 11.0609 13 10 13C8.93913 13 7.92172 12.5786 7.17157 11.8284C6.42143 11.0783 6 10.0609 6 9H4.5C4.5 10.4587 5.07946 11.8576 6.11091 12.8891C7.14236 13.9205 8.54131 14.5 10 14.5V17H11V14.5C12.4587 14.5 13.8576 13.9205 14.8891 12.8891C15.9205 11.8576 16.5 10.4587 16.5 9H15Z" fill="currentColor" opacity="0.7"/>
                <path d="M3.5 6.5C3.5 6.5 4.5 4 7 3M17.5 6.5C17.5 6.5 16.5 4 14 3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" opacity="0.6"/>
                <path d="M4.2 4.8L3.5 6.5L5.2 6.2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round" opacity="0.6"/>
                <path d="M16.8 4.8L17.5 6.5L15.8 6.2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round" opacity="0.6"/>
              </svg>
            </div>
            <span class="voice-control-label">Auto-listen</span>
          </div>

          <!-- Auto TTS output toggle -->
          <div
            v-if="ttsSupported"
            class="voice-control"
            @click="toggleAutoTts"
            :title="autoTtsOutput ? 'Disable auto-read responses' : 'Enable auto-read responses'"
          >
            <div class="voice-control-icon auto-voice-toggle" :class="{ 'auto-tts-active': autoTtsOutput }">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" opacity="0.8"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" opacity="0.7"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" opacity="0.5"/>
              </svg>
            </div>
            <span class="voice-control-label">Read aloud</span>
          </div>
        </div>

        <!-- Send icon -->
        <div class="send-icon"
          :class="{ 'send-icon-enabled': canSend }"
          @click="sendMessage">
          <img
            :src="require('@/assets/icons/send-icon.svg')"
            alt="Send Icon" />
        </div>
      </div>
    </div>

    <p class="disclaimer">AI can make mistakes. Please verify important information.</p>

    <!-- Voice error toast -->
    <transition name="voice-toast">
      <div v-if="voiceError" class="voice-error-toast" @click="voiceError = null">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span>{{ voiceError }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.txt', '.xlsx', '.csv'];
const ALL_EXTENSIONS = [...IMAGE_EXTENSIONS, ...DOCUMENT_EXTENSIONS];
const IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const DOC_MAX_SIZE = 20 * 1024 * 1024;

export default {
  name: 'ChatFooter',
  emits: ['send-message', 'load-conversation', 'auto-tts-changed'],
  props: {
    isThinking: Boolean,
    collapsed: Boolean,
    companyInformation: Object,
    selectedChatbot: {
      type: Object,
      default: null
    },
    sidebarWidth: {
      type: Number,
      default: 260
    }
  },
  data() {
    return {
      message: '',
      isDisabled: false,
      selectedFile: null,
      filePreview: null,
      isDragOver: false,
      // Voice input state
      isRecording: false,
      recognition: null,
      speechSupported: false,
      autoVoiceInput: false,
      autoVoiceStoppedByUser: false,
      // TTS output state
      ttsSupported: false,
      autoTtsOutput: false,
      // Voice error toast
      voiceError: null,
      voiceErrorTimer: null,
      // Desktop detection for voice feature visibility
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },
  mounted() {
    // Listen for viewport changes to show/hide voice controls
    this._onResize = () => { this.windowWidth = window.innerWidth; };
    window.addEventListener('resize', this._onResize);

    // Check if Web Speech API is available (desktop browsers)
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition && this.isDesktop) {
      this.speechSupported = true;
      this.initSpeechRecognition(SpeechRecognition);
    }
    // Check TTS support (only on desktop)
    this.ttsSupported = ('speechSynthesis' in window) && this.isDesktop;
    // Load voice settings if any voice feature is supported
    if (this.speechSupported || this.ttsSupported) {
      this.loadVoiceSettings();
    }
  },
  beforeUnmount() {
    // Clean up resize listener
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    // Clean up speech recognition on component destroy
    if (this.recognition) {
      this.recognition.abort();
      this.recognition = null;
    }
    // Clean up voice error timer
    if (this.voiceErrorTimer) {
      clearTimeout(this.voiceErrorTimer);
      this.voiceErrorTimer = null;
    }
  },
  computed: {
    footerStyle() {
      const w = this.collapsed ? 60 : this.sidebarWidth;
      return { width: 'calc(100% - ' + w + 'px)' };
    },
    isDesktop() {
      // Hide voice controls on mobile viewports (< 768px) or touch-only devices
      const wideEnough = this.windowWidth >= 768;
      // Check for touch-only devices (no mouse pointer)
      const hasMouse = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(pointer: fine)').matches
        : true;
      return wideEnough && hasMouse;
    },
    showVoiceControls() {
      // Voice controls require both API support AND desktop viewport
      return this.isDesktop;
    },
    supportsUpload() {
      return this.selectedChatbot && this.selectedChatbot.supports_file_upload;
    },
    acceptedFileTypes() {
      return ALL_EXTENSIONS.join(',');
    },
    canSend() {
      return (this.message.trim() !== '' || this.selectedFile) && !this.isDisabled;
    }
  },
  methods: {
    // --- Speech Recognition Methods ---

    initSpeechRecognition(SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = this.autoVoiceInput;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let transcript = '';
        let isFinal = false;
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            isFinal = true;
          }
        }
        if (isFinal) {
          this.appendTranscribedText(transcript);
          // Auto-send the transcribed text
          this.$nextTick(() => {
            if (this.canSend) {
              this.sendMessage();
            }
          });
        }
      };

      recognition.onend = () => {
        // In auto voice mode, restart listening unless user explicitly stopped
        if (this.autoVoiceInput && this.isRecording && !this.autoVoiceStoppedByUser) {
          // Brief delay before restarting to avoid rapid restarts
          setTimeout(() => {
            if (this.autoVoiceInput && this.isRecording && !this.autoVoiceStoppedByUser) {
              try {
                this.recognition.start();
              } catch (e) {
                console.warn('Auto-restart recognition error:', e);
                this.isRecording = false;
              }
            }
          }, 300);
        } else {
          this.isRecording = false;
        }
      };

      recognition.onerror = (event) => {
        const errorMessages = {
          'not-allowed': 'Microphone access denied. Please allow microphone access in your browser settings and try again.',
          'service-not-allowed': 'Speech recognition service is not allowed. Please check your browser permissions.',
          'network': 'Network error during transcription. Please check your internet connection and try again.',
          'audio-capture': 'No microphone detected. Please connect a microphone and try again.',
          'language-not-supported': 'Your language is not supported for speech recognition.',
        };

        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          // Microphone access denied - Feature #81
          this.isRecording = false;
          this.showVoiceError(errorMessages[event.error]);
        } else if (event.error === 'no-speech') {
          // In auto mode, no-speech is expected during pauses - let onend handle restart
          if (!this.autoVoiceInput) {
            this.isRecording = false;
            // Only show error in non-auto mode after a deliberate recording attempt
          }
        } else if (event.error === 'aborted') {
          // User-initiated abort, no error to show
          this.isRecording = false;
        } else {
          // Transcription failures - Feature #82 (network, audio-capture, etc.)
          this.isRecording = false;
          const message = errorMessages[event.error] || 'Voice transcription failed. Please try again or type your message instead.';
          this.showVoiceError(message);
          console.warn('Speech recognition error:', event.error);
        }
      };

      this.recognition = recognition;
    },

    toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    },

    startRecording() {
      if (!this.recognition) return;
      this.autoVoiceStoppedByUser = false;
      // Update continuous mode based on current auto voice setting
      this.recognition.continuous = this.autoVoiceInput;
      try {
        this.recognition.start();
        this.isRecording = true;
      } catch (e) {
        // If already started, stop and restart
        console.warn('Recognition start error:', e);
        this.isRecording = false;
        try {
          this.recognition.abort();
        } catch (abortErr) {
          // Ignore abort errors
        }
        // Show user-friendly error if it's a permission issue
        if (e.name === 'NotAllowedError') {
          this.showVoiceError('Microphone access denied. Please allow microphone access in your browser settings and try again.');
        } else if (e.name === 'InvalidStateError') {
          // Already running - just reset the state
        } else {
          this.showVoiceError('Unable to start voice input. Please try again.');
        }
      }
    },

    stopRecording() {
      if (!this.recognition) return;
      this.autoVoiceStoppedByUser = true;
      this.recognition.stop();
      this.isRecording = false;
    },

    showVoiceError(message) {
      // Clear any existing timer
      if (this.voiceErrorTimer) {
        clearTimeout(this.voiceErrorTimer);
      }
      this.voiceError = message;
      // Auto-dismiss after 5 seconds
      this.voiceErrorTimer = setTimeout(() => {
        this.voiceError = null;
        this.voiceErrorTimer = null;
      }, 5000);
    },

    async loadVoiceSettings() {
      try {
        const axios = (await import('@/axios-config')).default;
        const response = await axios.get('/api/voice/settings/', { withCredentials: true });
        if (response.data) {
          this.autoVoiceInput = response.data.auto_voice_input || false;
          this.autoTtsOutput = response.data.auto_tts_output || false;
          // Update recognition continuous setting if already initialized
          if (this.recognition) {
            this.recognition.continuous = this.autoVoiceInput;
          }
          // Emit auto-TTS state to parent for ChatConversation
          this.$emit('auto-tts-changed', this.autoTtsOutput);
        }
      } catch (e) {
        // Voice settings not critical, silently fail
        console.warn('Failed to load voice settings:', e);
      }
    },

    async toggleAutoTts() {
      this.autoTtsOutput = !this.autoTtsOutput;
      // Emit to parent so ChatConversation can know
      this.$emit('auto-tts-changed', this.autoTtsOutput);
      // Persist to server
      try {
        const axios = (await import('@/axios-config')).default;
        await axios.patch('/api/voice/settings/', {
          auto_tts_output: this.autoTtsOutput
        }, { withCredentials: true });
      } catch (e) {
        console.warn('Failed to save voice settings:', e);
      }
    },

    async toggleAutoVoice() {
      this.autoVoiceInput = !this.autoVoiceInput;

      // Update recognition continuous mode immediately
      if (this.recognition) {
        this.recognition.continuous = this.autoVoiceInput;
      }

      // If turning off auto mode while recording, stop and restart in non-continuous mode
      if (!this.autoVoiceInput && this.isRecording) {
        this.stopRecording();
      }

      // Persist to server
      try {
        const axios = (await import('@/axios-config')).default;
        await axios.patch('/api/voice/settings/', {
          auto_voice_input: this.autoVoiceInput
        }, { withCredentials: true });
      } catch (e) {
        console.warn('Failed to save voice settings:', e);
      }
    },

    appendTranscribedText(text) {
      if (!text || !text.trim()) return;

      // Add a space before appending if there's already text
      const currentText = this.message.trim();
      const newText = currentText
        ? currentText + ' ' + text.trim()
        : text.trim();

      this.message = newText;

      // Update the contenteditable editor with the new text
      this.$nextTick(() => {
        if (this.$refs.editor) {
          this.$refs.editor.innerText = this.message;
          this.resizeInput();
          // Place cursor at end
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(this.$refs.editor);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
          this.$refs.editor.focus();
        }
      });
    },

    // --- Existing Methods ---

    onInput() {
      this.message = this.$refs.editor.innerText;

      if (this.message.trim() === '') {
        this.$refs.editor.innerHTML = '';
      }
      this.resizeInput();
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.attachFile(file);
      }
      // Reset so the same file can be re-selected
      event.target.value = '';
    },

    attachFile(file) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALL_EXTENSIONS.includes(ext)) {
        alert(`Unsupported file type: ${ext}. Allowed: ${ALL_EXTENSIONS.join(', ')}`);
        return;
      }
      const isImage = IMAGE_EXTENSIONS.includes(ext);
      const maxSize = isImage ? IMAGE_MAX_SIZE : DOC_MAX_SIZE;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        alert(`File too large. Maximum size: ${maxMB}MB`);
        return;
      }

      this.selectedFile = file;

      if (isImage) {
        const reader = new FileReader();
        reader.onload = (e) => { this.filePreview = e.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.filePreview = null;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.filePreview = null;
    },

    onDragOver() {
      if (this.supportsUpload) {
        this.isDragOver = true;
      }
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(event) {
      this.isDragOver = false;
      if (!this.supportsUpload) return;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.attachFile(file);
      }
    },

    onPaste(event) {
      if (!this.supportsUpload) return;
      const items = event.clipboardData && event.clipboardData.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Give pasted images a name
            const ext = file.type.split('/')[1] || 'png';
            const named = new File([file], `pasted-image.${ext}`, { type: file.type });
            this.attachFile(named);
          }
          return;
        }
      }
    },

    sendMessage() {
      if (!this.canSend) return;

      // Stop recording if active (but remember auto mode for re-enable after response)
      if (this.isRecording) {
        this.stopRecording();
      }

      this.isDisabled = true;
      const msg = this.message.trim();
      const file = this.selectedFile;

      if (file) {
        this.$emit('send-message', { message: msg, file: file, preview: this.filePreview });
      } else {
        this.$emit('send-message', msg);
      }

      this.message = '';
      this.selectedFile = null;
      this.filePreview = null;
      this.$nextTick(() => {
        this.$refs.editor.innerHTML = '';
        this.resizeInput();
      });
    },

    enableInput() {
      this.isDisabled = false;
      // Resume recording in auto-voice mode after response completes
      if (this.autoVoiceInput && !this.isRecording && this.recognition) {
        this.$nextTick(() => {
          this.startRecording();
        });
      }
    },
    resizeInput() {
      const editor = this.$refs.editor;
      if (!editor) return;
      editor.style.height = 'auto';

      if (!this.message.trim()) {
        editor.style.height = '3.1rem';
      } else {
        editor.style.height = editor.scrollHeight + 'px';
      }
    },
    loadConversation(conversation) {
      this.$emit('load-conversation', conversation);
    }
  }
};
</script>

<style scoped>
.footer-container {
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--bg-topbar);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  position: fixed;
  bottom: 0;
  right: 0;
  padding: var(--space-md) var(--space-lg) var(--space-sm);
  box-sizing: border-box;
}

.input-container {
  width: 100%;
  max-width: 800px;
  background: var(--bg-surface);
  border-radius: var(--radius-lg);
  padding: var(--space-xs);
  box-sizing: border-box;
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-card);
  transition: var(--transition-colors), var(--transition-shadow);
}

.input-container:focus-within {
  border-color: var(--border-focus);
  box-shadow: var(--shadow-input-focus);
}

.input-container.drag-over {
  border-color: var(--accent-primary);
  background: var(--accent-muted);
}

.disclaimer {
  margin: var(--space-xs) 0 0 0;
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-align: center;
  line-height: 1;
  user-select: none;
}

.input-content {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  width: 100%;
}

.icon-container {
  display: flex;
  gap: var(--space-sm);
}

.icon {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.upload-icon {
  cursor: pointer;
  opacity: 0.5;
  transition: opacity var(--duration-base) var(--ease-default),
              background-color var(--duration-base) var(--ease-default);
  margin-left: var(--space-sm);
  padding: var(--space-xs);
  border-radius: var(--radius-pill);
}

.upload-icon:hover {
  opacity: 1;
  background-color: var(--bg-surface-hover);
}

.file-preview-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-xs) var(--space-md);
  background: var(--bg-surface-raised);
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  border-bottom: 1px solid var(--border-subtle);
}

.file-preview-content {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  overflow: hidden;
}

.file-preview-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: var(--radius-sm);
}

.file-preview-icon {
  font-size: 24px;
}

.file-preview-name {
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.file-preview-remove {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0 var(--space-xs);
  border-radius: var(--radius-sm);
  transition: var(--transition-colors);
}

.file-preview-remove:hover {
  color: var(--text-primary);
  background: var(--bg-surface-hover);
}

.input-text {
  flex: 1;
  min-height: 2.5rem;
  max-height: 450px;
  font-size: var(--text-base);
  font-family: var(--font-sans);
  line-height: var(--leading-normal);
  padding: var(--space-md) var(--space-md);
  resize: none;
  border: none;
  outline: none;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
  color: var(--text-primary);
  background: transparent;
}

.input-text[contenteditable="false"] {
  cursor: pointer !important;
  caret-color: transparent !important;
  user-select: none;
}

[contenteditable]:empty::before {
  content: attr(data-placeholder);
  color: var(--text-placeholder);
  pointer-events: none;
}

.input-text::-webkit-scrollbar {
  width: 6px;
}

.input-text::-webkit-scrollbar-track {
  background: transparent;
}

.input-text::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: var(--radius-pill);
}

.input-text::-webkit-scrollbar-thumb:hover {
  background-color: var(--scrollbar-thumb-hover);
}

/* Voice controls group */
.voice-controls {
  display: flex;
  align-items: flex-end;
  margin-bottom: 4px;
  gap: 2px;
  border-left: 1px solid var(--border-subtle);
  padding-left: var(--space-sm);
  margin-left: var(--space-xs);
  align-self: flex-end;
  flex-shrink: 0;
}

.voice-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: var(--space-xs) var(--space-xs);
  border-radius: var(--radius-sm);
  transition: var(--transition-colors);
  gap: 2px;
}

.voice-control:hover {
  background: var(--bg-surface-hover);
}

.voice-control-icon {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-control-label {
  font-size: 9px;
  font-weight: var(--font-medium);
  letter-spacing: var(--tracking-wide);
  color: inherit;
  white-space: nowrap;
  line-height: 1;
  opacity: 0.8;
}

/* Auto voice toggle */
.auto-voice-toggle {
  width: 22px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  transition: var(--transition-colors);
  color: var(--text-muted);
  flex-shrink: 0;
  border: 1.5px solid transparent;
}

.auto-voice-toggle:hover {
  color: var(--text-primary);
}

.auto-voice-active {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.3);
}

.auto-voice-active:hover {
  color: #16a34a;
  background: rgba(34, 197, 94, 0.15);
  border-color: rgba(34, 197, 94, 0.5);
}

.auto-tts-active {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
}

.auto-tts-active:hover {
  color: #2563eb;
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.5);
}

/* Microphone button */
.mic-icon {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  transition: var(--transition-colors);
  color: var(--text-muted);
  flex-shrink: 0;
}

.mic-icon-hover:hover {
  color: var(--text-primary);
  background: var(--bg-surface-hover);
}

.mic-active {
  background: #ef4444;
  color: #fff;
  animation: mic-pulse 1.5s ease-in-out infinite;
}

.mic-active:hover {
  background: #dc2626;
  color: #fff;
}

@keyframes mic-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }
}

/* Voice error toast */
.voice-error-toast {
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--radius-md);
  color: #dc2626;
  font-size: var(--text-sm);
  font-family: var(--font-sans);
  cursor: pointer;
  box-shadow: var(--shadow-float);
  z-index: 20;
  max-width: 90%;
  width: fit-content;
  white-space: nowrap;
}

.voice-error-toast svg {
  flex-shrink: 0;
}

/* Voice toast transition */
.voice-toast-enter-active {
  transition: opacity var(--duration-slow) var(--ease-out),
              transform var(--duration-slow) var(--ease-out);
}

.voice-toast-leave-active {
  transition: opacity var(--duration-base) var(--ease-in),
              transform var(--duration-base) var(--ease-in);
}

.voice-toast-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.voice-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.send-icon {
  width: 34px;
  height: 34px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  align-self: flex-end;
  margin-bottom: var(--space-xs);
  margin-right: var(--space-xs);
  border-radius: 50%;
  transition: var(--transition-colors), var(--transition-shadow), var(--transition-transform);
  color: var(--text-muted);
  flex-shrink: 0;
}

.send-icon img {
  width: 18px;
  height: 18px;
}

.send-icon-enabled {
  background: var(--accent-primary);
  color: var(--text-on-accent);
  box-shadow: var(--shadow-sm);
}

.send-icon-enabled:hover {
  background: var(--accent-hover);
  box-shadow: var(--shadow-card);
  transform: scale(1.05);
}

.send-icon-enabled:active {
  transform: scale(0.95);
}

.send-icon-enabled img {
  filter: brightness(0) invert(1);
}

.thinking-indicator {
  display: flex;
  gap: var(--space-xs);
  margin: var(--space-md) 0;
  padding: var(--space-md) 0;
  margin-left: var(--space-lg);
}

.dot {
  width: 7px;
  height: 7px;
  background: var(--accent-primary);
  border-radius: 50%;
  animation: thinkingWave 1.4s ease-in-out infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.15s;
}

.dot:nth-child(3) {
  animation-delay: 0.3s;
}

/* Active state label coloring */
.voice-control:has(.auto-voice-active) {
  color: #22c55e;
}

.voice-control:has(.auto-tts-active) {
  color: #3b82f6;
}

.voice-control:has(.mic-active) {
  color: #fff;
}

.voice-control:has(.mic-active) .voice-control-label {
  color: #ef4444;
}

/* Hide voice controls on mobile viewports (CSS safety fallback) */
@media (max-width: 767px) {
  .voice-controls {
    display: none !important;
  }
}
</style>
